/**
 * Number and time formatting, shared by every results screen.
 *
 * These used to live at the bottom of Results.tsx, whose own comment said they
 * would move "when a second screen needs them". Three now do.
 *
 * The rule they all follow: an absent value renders as an em dash, never as a
 * zero. A fabricated 0 is how a free setup gets invented at the top of a cost
 * leaderboard, how an agent that produced no readable event stream is credited
 * with solving a challenge in no steps, and how "we cannot see" becomes "it did
 * nothing". Null is a fact and it is displayed as one.
 */

/** The em dash every unknown renders as. */
export const UNKNOWN = "—";

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === undefined || seconds === null) return UNKNOWN;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

/**
 * A null cost means the model had no pricing entry, and renders as unknown
 * rather than as zero.
 */
export function formatCost(usd: number | null | undefined): string {
  if (usd === undefined || usd === null) return UNKNOWN;
  if (usd === 0) return "$0.00";
  return usd < 0.01 ? "<$0.01" : `$${usd.toFixed(2)}`;
}

/**
 * Tokens, abbreviated. A leaderboard column holding 15,589,465 in full makes
 * every other column narrower for no information.
 */
export function formatTokens(tokens: number | null | undefined): string {
  if (tokens === undefined || tokens === null) return UNKNOWN;
  if (tokens < 1_000) return String(tokens);
  if (tokens < 1_000_000) return `${(tokens / 1_000).toFixed(1)}k`;
  return `${(tokens / 1_000_000).toFixed(1)}M`;
}

/** A plain count, or the em dash when the archive did not say. */
export function formatCount(n: number | null | undefined): string {
  if (n === undefined || n === null) return UNKNOWN;
  return n.toLocaleString();
}

/** A percentage with no decimals, which is the precision a pass rate has. */
export function formatPercent(fraction: number | null | undefined): string {
  if (fraction === undefined || fraction === null) return UNKNOWN;
  return `${Math.round(fraction * 100)}%`;
}

export function formatTimestamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
